/**
 * Meeting Tools — Attendance & Engagement (real presence + grasp + telemetry).
 *
 * List view: student table only.
 * Row click → student detail (with Back to listing).
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  fetchAttendanceEngagement,
  type AttendanceEngagementStudent,
  type AttendanceEngagementSummary,
} from "./attendanceEngagementApi";
import {
  isAuthHttpError,
  isAuthSessionExpired,
} from "../utils/authSessionGuard";

type FilterId = "all" | "attendees" | "absentees" | "engaged";

type Props = {
  sessionId: string;
  pollMs?: number;
  /** Open panel already filtered (e.g. from top-bar counts). */
  initialFilter?: FilterId;
};

export type AttendanceEngagementFilter = FilterId;

const emptySummary: AttendanceEngagementSummary = {
  total_students: 0,
  attendees: 0,
  attendees_pct: 0,
  absentees: 0,
  absentees_pct: 0,
  engaged: 0,
  engaged_pct: 0,
  active_now: 0,
  active_now_pct: 0,
};

function pctColor(pct: number): string {
  if (pct >= 70) return "#34d399";
  if (pct >= 40) return "#fbbf24";
  return "#f87171";
}

function FilterChip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: "none",
        borderRadius: 999,
        padding: "6px 12px",
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
        background: active ? "#2563eb" : "rgba(255,255,255,0.06)",
        color: active ? "#fff" : "#94a3b8",
      }}
    >
      {label}
    </button>
  );
}

function SummaryCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: string;
}) {
  return (
    <div
      style={{
        minWidth: 0,
        maxWidth: "100%",
        overflow: "hidden",
        boxSizing: "border-box",
        borderRadius: 10,
        border: "1px solid rgba(255,255,255,0.08)",
        background: "rgba(255,255,255,0.03)",
        padding: "10px 10px",
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.2,
          color: "#64748b",
          textTransform: "uppercase",
          lineHeight: 1.3,
          overflowWrap: "break-word",
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: "6px 0 0",
          fontSize: 14,
          fontWeight: 700,
          color: accent || "#e2e8f0",
          lineHeight: 1.3,
          overflowWrap: "anywhere",
          wordBreak: "break-word",
          whiteSpace: "pre-line",
        }}
      >
        {value}
      </p>
    </div>
  );
}

function DetailRow({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <span style={{ color: "#94a3b8" }}>{label}</span>
      <span
        style={{
          color: strong ? "#e2e8f0" : "#cbd5e1",
          fontWeight: strong ? 700 : 500,
          textAlign: "right",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function StudentDetailView({
  student,
  onBack,
}: {
  student: AttendanceEngagementStudent;
  onBack: () => void;
}) {
  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={onBack}
        style={{
          alignSelf: "flex-start",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 8,
          background: "rgba(255,255,255,0.04)",
          color: "#e2e8f0",
          fontSize: 12,
          fontWeight: 600,
          padding: "6px 10px",
          cursor: "pointer",
        }}
      >
        <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>
          ←
        </span>
        Back to students
      </button>

      <div
        className="meeting-tool-hide-scrollbar"
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.08)",
          background: "rgba(255,255,255,0.03)",
          padding: 16,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#f8fafc" }}>
          {student.name}
        </p>
        <div
          style={{
            marginTop: 14,
            display: "grid",
            gap: 10,
            fontSize: 13,
          }}
        >
          <DetailRow label="Email" value={student.email || "—"} />
          <DetailRow label="USN" value={student.usn || "—"} />
          <DetailRow label="Phone" value={student.phone || "—"} />
          <DetailRow label="Batch" value={student.batch || "—"} />
        </div>
        <div
          style={{
            marginTop: 16,
            paddingTop: 14,
            borderTop: "1px solid rgba(255,255,255,0.08)",
            display: "grid",
            gap: 10,
            fontSize: 13,
          }}
        >
          <DetailRow
            label="Attendance"
            value={`${student.attendance_pct}%`}
            strong
          />
          <DetailRow
            label="Attended time"
            value={`${student.attended_minutes} min`}
            strong
          />
          <DetailRow
            label="Session duration"
            value={`${student.session_duration_minutes} min`}
            strong
          />
          <DetailRow
            label="Engagement"
            value={`${student.engagement_pct}%`}
            strong
          />
          <DetailRow
            label="Away (blur / tab hidden)"
            value={
              student.away_minutes == null
                ? "—"
                : `${student.away_minutes} min`
            }
            strong
          />
        </div>
      </div>
    </div>
  );
}

export function AttendanceEngagementPanel({
  sessionId,
  pollMs = 30000,
  initialFilter = "all",
}: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState<FilterId>(initialFilter);
  const [summary, setSummary] =
    useState<AttendanceEngagementSummary>(emptySummary);
  const [students, setStudents] = useState<AttendanceEngagementStudent[]>([]);
  /** null = listing; set = student detail view */
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    setFilter(initialFilter);
    setSelectedId(null);
  }, [initialFilter]);

  useEffect(() => {
    let cancelled = false;
    let intervalId: number | undefined;
    let inFlight = false;
    const stop = () => {
      cancelled = true;
      if (intervalId != null) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
    };
    const load = async () => {
      if (cancelled || inFlight || isAuthSessionExpired()) {
        if (isAuthSessionExpired()) stop();
        return;
      }
      inFlight = true;
      try {
        const report = await fetchAttendanceEngagement(sessionId);
        if (cancelled) return;
        setSummary(report.summary);
        setStudents(report.students);
        setError("");
        // Keep selection only if that student still exists; never auto-open detail
        setSelectedId((prev) => {
          if (prev && report.students.some((s) => s.student_id === prev)) {
            return prev;
          }
          return null;
        });
      } catch (err) {
        if (isAuthHttpError(err) || isAuthSessionExpired()) {
          stop();
          return;
        }
        console.error("attendance-engagement load failed", err);
        if (!cancelled) setError("Unable to load attendance data.");
      } finally {
        inFlight = false;
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    intervalId = window.setInterval(() => void load(), pollMs);
    return () => stop();
  }, [sessionId, pollMs]);

  const filtered = useMemo(() => {
    if (filter === "attendees") return students.filter((s) => s.present);
    if (filter === "absentees") return students.filter((s) => !s.present);
    if (filter === "engaged") {
      return students.filter((s) => s.engagement_pct > 0 || s.engaged);
    }
    return students;
  }, [students, filter]);

  const selected =
    students.find((s) => s.student_id === selectedId) || null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minHeight: 0,
        height: "100%",
        color: "#e2e8f0",
      }}
    >
      {selected ? (
        <StudentDetailView
          student={selected}
          onBack={() => setSelectedId(null)}
        />
      ) : (
        <>
          <div>
            <p style={{ margin: 0, fontSize: 12, color: "#64748b" }}>
              Monitor student attendance and engagement during this live
              session.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(min(100%, 132px), 1fr))",
              gap: 8,
              alignItems: "stretch",
            }}
          >
            <SummaryCard
              label="Total students"
              value={String(summary.total_students)}
            />
            <SummaryCard
              label="Attendees"
              value={`${summary.attendees}/${summary.total_students}\n(${summary.attendees_pct}%)`}
              accent="#34d399"
            />
            <SummaryCard
              label="Absentees"
              value={`${summary.absentees}/${summary.total_students}\n(${summary.absentees_pct}%)`}
              accent="#f87171"
            />
            <SummaryCard
              label="Engagement score"
              value={`${summary.engaged}/${summary.total_students}\n(${summary.engaged_pct}%)`}
              accent="#60a5fa"
            />
            <SummaryCard
              label="Active now"
              value={`${summary.active_now}/${summary.total_students}\n(${summary.active_now_pct}%)`}
              accent="#34d399"
            />
          </div>

          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <FilterChip
              active={filter === "all"}
              label="All students"
              onClick={() => setFilter("all")}
            />
            <FilterChip
              active={filter === "attendees"}
              label="Attendees"
              onClick={() => setFilter("attendees")}
            />
            <FilterChip
              active={filter === "absentees"}
              label="Absentees"
              onClick={() => setFilter("absentees")}
            />
            <FilterChip
              active={filter === "engaged"}
              label="Has engagement"
              onClick={() => setFilter("engaged")}
            />
          </div>

          {error ? (
            <p style={{ margin: 0, fontSize: 12, color: "#f87171" }}>{error}</p>
          ) : null}

          <div
            className="meeting-tool-hide-scrollbar"
            style={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.08)",
              background: "rgba(0,0,0,0.2)",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 12,
              }}
            >
              <thead>
                <tr style={{ color: "#64748b", textAlign: "left" }}>
                  {[
                    "Student",
                    "Attendance",
                    "Attended",
                    "Engagement",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "10px 8px",
                        fontWeight: 700,
                        fontSize: 10,
                        letterSpacing: 0.3,
                        textTransform: "uppercase",
                        borderBottom: "1px solid rgba(255,255,255,0.08)",
                        position: "sticky",
                        top: 0,
                        background: "#141820",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && students.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        padding: 24,
                        textAlign: "center",
                        color: "#64748b",
                      }}
                    >
                      Loading…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        padding: 24,
                        textAlign: "center",
                        color: "#64748b",
                      }}
                    >
                      No students in this filter.
                    </td>
                  </tr>
                ) : (
                  filtered.map((s) => (
                    <tr
                      key={s.student_id}
                      onClick={() => setSelectedId(s.student_id)}
                      style={{
                        cursor: "pointer",
                        background: "transparent",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background =
                          "rgba(37,99,235,0.12)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      <td style={{ padding: "10px 8px", fontWeight: 600 }}>
                        {s.name}
                      </td>
                      <td
                        style={{
                          padding: "10px 8px",
                          color: pctColor(s.attendance_pct),
                          fontWeight: 700,
                        }}
                      >
                        {s.attendance_pct}%
                      </td>
                      <td style={{ padding: "10px 8px", color: "#94a3b8" }}>
                        {s.attended_minutes} / {s.session_duration_minutes} min
                      </td>
                      <td
                        style={{
                          padding: "10px 8px",
                          color: pctColor(s.engagement_pct),
                          fontWeight: 700,
                        }}
                      >
                        {s.engagement_pct}%
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
